import { useCallback, useMemo, useState } from 'react';

import type { QueryHookResult } from '@change-corgi/core-react/async';
import { useQuery } from '@change-corgi/core-react/async';
import { createMandatoryContext } from '@change-corgi/core-react/context';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { HttpError, RedirectError } from '#src/shared/error';

import { useMappedLoadedState } from '#src/app/shared/hooks/state';

import type { ContextData, Map, Options, Result, RethrowableErrorState } from './types';
import { useCheckHookDeps } from './useCheckHookDeps';

function createContext<DATA extends Record<string, unknown>, DATA_PROPERTY extends string = 'data'>(
	name: string,
	dataProperty: DATA_PROPERTY,
) {
	return createMandatoryContext<
		ContextData<DATA>,
		Record<DATA_PROPERTY, ContextData<DATA>>
		// oxlint-disable-next-line @typescript-eslint/no-unsafe-argument
	>(undefined, {
		name,
		processProviderProps: (props: Record<DATA_PROPERTY, ContextData<DATA>>): ContextData<DATA> => props[dataProperty],
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} as any);
}

export function createAsyncDataContext<
	DATA extends Record<string, unknown>,
	PARAMS extends unknown[] = [],
	DATA_PROPERTY extends string = 'data',
	ERROR extends Map = EmptyIntersection,
>({
	name,
	getData,
	dataProperty: dataPropertyP,
	errorHandler: errorHandlerP,
	hookDeps,
	loadingBetweenQueries,
}: Options<DATA, PARAMS, DATA_PROPERTY, ERROR>): Result<DATA, PARAMS, DATA_PROPERTY, ERROR> {
	const dataProperty = dataPropertyP || ('data' as DATA_PROPERTY);
	const { Context, Provider, useContext } = createContext<DATA, DATA_PROPERTY>(name, dataProperty);

	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	const errorHandler: (e: any) => ERROR = (error: unknown) => {
		// not catching HTTP errors that should instead be handled by the server, or the ErrorBoundary
		if (error instanceof HttpError || error instanceof RedirectError) {
			throw error;
		}
		return errorHandlerP ? errorHandlerP(error) : ({} as ERROR);
	};

	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	const hookErrorHandler: (e: any) => ERROR = (error: unknown) => {
		try {
			return errorHandler(error);
		} catch (err) {
			// using a "hidden" field in the returned state, because the error is thrown within a useEffect
			// which would not fall into a ErrorBoundary
			return { __errorToRethrow: err } satisfies RethrowableErrorState as unknown as ERROR;
		}
	};

	const res = {
		Context,
		Provider,
		useContext,
		useAsyncData: (...params: PARAMS) => {
			const utilityContext = useUtilityContext();
			const track = useTracking();
			const [refreshCount, setRefreshCount] = useState(0);

			const deps = hookDeps ? hookDeps(...params) : params;

			useCheckHookDeps(deps, utilityContext);

			const refreshData = useCallback(() => setRefreshCount((count) => count + 1), []);

			const utilityContextWithTracker = useMemo(() => ({ ...utilityContext, track }), [utilityContext, track]);

			const dataState = useQuery<Record<DATA_PROPERTY, DATA>, ERROR>(
				// `dataProperty`'s type is the generic `DATA_PROPERTY`, so TS widens a computed-key object
				// literal built from it to a `{ [x: string]: DATA }` index signature rather than keeping the
				// specific key — assert the real shape (a single `dataProperty` key) instead.
				async () =>
					({
						[dataProperty]: await getData(utilityContextWithTracker, ...params),
					}) as Record<DATA_PROPERTY, DATA>,

				[...deps, refreshCount, utilityContextWithTracker],
				{
					errorHandler: hookErrorHandler,
					loadingBetweenQueries,
				},
			);

			const hookRes = useMappedLoadedState(dataState, (data) => {
				// Same generic-key limitation as the `useQuery` call above: `dataProperty`'s type
				// (`DATA_PROPERTY`) isn't recognized as indexing the inferred, mapped loaded-state shape,
				// even though it's the one real key present at runtime — assert it explicitly.
				const typedData = data as Record<DATA_PROPERTY, DATA>;
				return {
					[dataProperty]: {
						data: typedData[dataProperty],
						actions: {
							refreshData,
						},
					},
				};
			}) as QueryHookResult<Record<DATA_PROPERTY, ContextData<DATA>>, ERROR>;

			if (hookRes.status === 'error' && (hookRes as unknown as RethrowableErrorState).__errorToRethrow) {
				throw (hookRes as unknown as RethrowableErrorState).__errorToRethrow;
			}

			return hookRes;
		},
	};

	return res;
}
